import {
  Badge,
  Box,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  ListItem,
  Select,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useRequestResources } from '../../../services/hooks/Resources/useRequestResources';
import MainCard from '../../../components/MainCard/Index';
import { badgeColor } from '../../../utils/badgeFunction';
import {
  ISelectiveProcess,
  useRequestSelectiveProcess,
} from '../../../services/hooks/SelectiveProcess/useRequestSelectiveProcess';
import ResourceMobilePage from './mobile/list';
import React, { useContext, useMemo, useState } from 'react';
import { ContextAuth } from '../../../contexts/Authentication';
import { MdOutlineGavel, MdSearch } from 'react-icons/md';
import { GrResources } from 'react-icons/gr';
import { IoIosArrowForward } from 'react-icons/io';
import { RiSignalWifiErrorLine } from 'react-icons/ri';
import { HiOutlineSearchCircle } from 'react-icons/hi';
import { LiaHandPointer } from 'react-icons/lia';

const Resources: React.FC = () => {
  const [selectiveProcess, setSelectiveProcess] = useState<ISelectiveProcess>();
  const [filter, setFilter] = useState<string>('');

  const { data, error, isLoading } = useRequestResources();
  const spList = useRequestSelectiveProcess(undefined, undefined, true);
  const { isMobile } = useContext(ContextAuth);
  const navigate = useNavigate();

  const selectiveProcessList = useMemo(() => {
    return (
      spList.data?.filter((sp) => data?.some((resource) => resource.selective_process_id === sp.id)) ?? []
    );
  }, [data, spList]);

  const resources = data?.find((resource) => resource.selective_process_id === selectiveProcess?.id);

  if (isMobile) return <ResourceMobilePage />;

  return (
    <MainCard
      height="100%"
      title="ÁREA DO CANDIDATO"
      subtitle="- MEUS RECURSOS"
      description="Acesse ou solicite seus recursos e impugnações de forma simples, prática e organizada."
    >
      {(isLoading && (
        <Stack height="100%" justify="center" align="center">
          <Spinner size="lg" />
        </Stack>
      )) ||
        (error && (
          <Stack height="100%" color="red" justify="center" align="center">
            <RiSignalWifiErrorLine size={50} />
            <Text fontWeight="bold" fontSize="16px">
              Erro ao trazer os dados!
            </Text>
          </Stack>
        )) ||
        (data && (
          <>
            <Card mx={8} mb={5} boxShadow="md">
              <CardBody display="flex" gap={20} alignItems="flex-end">
                <FormControl w="60%">
                  <FormLabel fontSize="xs" mb={1}>
                    Processo seletivo
                  </FormLabel>
                  <Select
                    placeholder=""
                    value={selectiveProcess?.id ?? ''}
                    onChange={(e) => setSelectiveProcess(spList.data?.find((sp) => sp.id === e.currentTarget.value))}
                    fontSize="sm"
                    h="35px"
                    isDisabled={!spList.data}
                  >
                    <option hidden>Selecione o item...</option>
                    {selectiveProcessList?.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <InputGroup w="40%">
                  <InputLeftElement h="35px" pointerEvents="none">
                    <MdSearch size={20} color="gray" />
                  </InputLeftElement>
                  <Input
                    isDisabled={!selectiveProcessList}
                    value={filter}
                    onChange={(e) => setFilter(e.currentTarget.value ?? '')}
                    h="35px"
                    placeholder="Buscar..."
                  />
                </InputGroup>
              </CardBody>
            </Card>
            <Tabs onChange={() => setFilter('')} px={8}>
              <TabList>
                <Tab color="gray.400" _selected={{ color: '#01aadd', borderColor: '#01aadd' }} gap={1}>
                  <Box>
                    <GrResources size={16} />
                  </Box>
                  Recursos
                </Tab>
                <Tab color="gray.400" _selected={{ color: '#01aadd', borderColor: '#01aadd' }} gap={1}>
                  <Box>
                    <MdOutlineGavel />
                  </Box>
                  Impugnações
                </Tab>
              </TabList>
              <TabPanels boxShadow="lg" my={4} py={1} pb={5}>
                <TabPanel>
                  {(selectiveProcess && (
                    <Stack mt={2} mx={3} gap={3}>
                      <Text fontSize="md" mb={2} fontWeight="semibold">
                        {selectiveProcess?.name || 'Não informado'}
                      </Text>
                      <Stack ml={3}>
                        {resources?.vacancies.map((vacancy) => {
                          const normalizedFilter = filter?.toLowerCase() ?? '';

                          const filteredResources = !normalizedFilter
                            ? vacancy.resources
                            : vacancy.resources.filter((item) =>
                                item.period_identification?.toLowerCase().includes(normalizedFilter),
                              );

                          if (!filteredResources?.length)
                            return (
                              <Stack height="100%" minH="400px" gap={5} color="gray" justify="center" align="center">
                                <HiOutlineSearchCircle size={50} />
                                <Text fontSize="md">Não foi encontrado nenhum recurso!</Text>
                              </Stack>
                            );

                          return (
                            <React.Fragment key={vacancy.vacancy_id}>
                              <Text
                                w="fit-content"
                                borderColor="#ffbb00"
                                borderBottomWidth={2}
                                px={2}
                                py={1}
                                pr={5}
                                fontSize="md"
                              >
                                {vacancy.vacancy_name}
                              </Text>

                              <UnorderedList listStyleType="none" m={0} mt={5} spacing={2}>
                                {filteredResources.map((item) => (
                                  <ListItem
                                    key={item.resource_id}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    border="1px solid #c5c5c5"
                                    p={5}
                                    px={7}
                                    rounded="md"
                                    transition="0.4s"
                                    cursor="pointer"
                                    onClick={() => navigate(`/painel/recursos/mostrar-mais/${item.resource_id}`)}
                                    _hover={{
                                      backgroundColor: 'blue.50',
                                      borderColor: 'blue.200',
                                    }}
                                    sx={{
                                      '&:hover > div + div': {
                                        backgroundColor: '#9fd4f3',
                                        border: '1px solid #9fd4f3',
                                        color: '#ffffff',
                                        transition: '0.3s',
                                      },
                                    }}
                                  >
                                    <Stack gap={1}>
                                      <Text fontSize="sm">{item.period_identification}</Text>

                                      <Flex gap={2}>
                                        <Text color="gray" fontSize="xs" mt={1}>
                                          {item.period}
                                        </Text>

                                        <Box>
                                          <Badge
                                            fontSize="11px"
                                            bg={badgeColor(item?.resource_is_open)?.bgColor}
                                            color={badgeColor(item?.resource_is_open)?.color}
                                          >
                                            {item.resource_is_open}
                                          </Badge>
                                        </Box>
                                      </Flex>
                                    </Stack>

                                    <Box rounded="full" p="10px">
                                      <IoIosArrowForward size={18} />
                                    </Box>
                                  </ListItem>
                                ))}
                              </UnorderedList>
                            </React.Fragment>
                          );
                        })}
                      </Stack>
                    </Stack>
                  )) || (
                    <Stack height="100%" minH="400px" gap={5} color="gray" justify="center" align="center">
                      <LiaHandPointer size={50} />
                      <Text fontSize="md">Selecione o processo seletivo para ver os recursos!</Text>
                    </Stack>
                  )}
                </TabPanel>
                <TabPanel>
                  <Stack mt={2} mx={3}>
                    <Text fontSize="md" fontWeight="semibold">
                      IMPUGNAÇÃO CONTRA O EDITAL
                    </Text>
                    <UnorderedList listStyleType="none" m={0} mt={5} spacing={2}>
                      {spList?.data?.map((item) => (
                        <ListItem
                          display="flex"
                          gap={10}
                          justifyContent="space-between"
                          alignItems="center"
                          border="1px solid #c5c5c5"
                          p={5}
                          px={7}
                          rounded="md"
                          transition="0.4s"
                          cursor="pointer"
                          onClick={() => navigate(`/painel/impugnacao/${item?.id}`)}
                          _hover={{
                            backgroundColor: 'blue.50',
                            borderColor: 'blue.200',
                          }}
                          sx={{
                            '&:hover > div + div': {
                              backgroundColor: '#9fd4f3',
                              border: '1px solid #9fd4f3',
                              color: '#ffffff',
                              transition: '0.3s',
                            },
                          }}
                        >
                          <Stack gap={1}>
                            <Text fontSize="sm">{item.name}</Text>
                          </Stack>
                          <Box rounded="full" p="10px">
                            <IoIosArrowForward size={18} />
                          </Box>
                        </ListItem>
                      ))}
                    </UnorderedList>
                  </Stack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </>
        )) || (
          <Stack p={10}>
            <Text>VOCÊ AINDA NÃO POSSUI RECURSOS PROTOCOLADOS.</Text>
          </Stack>
        )}
    </MainCard>
  );
};

export default Resources;
